import { useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useOutletContext } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useTabParam } from '@structural-climbing/ui'
import { ChipTabs } from '../design/ChipTabs'
import type { NewsItem } from '@structural-climbing/ui'
import { useNews } from '../features/news/useNews'
import { splitFeed, unseenNewsCount } from '../features/news/newsSelectors'
import { sortNews } from '../features/news/sortNews'
import { NewsCard, TabTitle } from '../features/news/NewsCard'
import { useSeenNews } from '../context/SeenNewsContext'
import { Page } from '../design/Page'
import { EmptyState } from '../design/EmptyState'
import { SkeletonCard } from '../design/Skeleton'
import { staggerIndex } from '../design/motion'
import type { GymContext } from '../layouts/GymLayout'

const NEWS_TABS = ['gym', 'global', 'updates'] as const
type NewsTab = (typeof NEWS_TABS)[number]

function NewsList({ items, emptyLabel }: { items: NewsItem[]; emptyLabel: string }) {
  const sorted = sortNews(items)

  if (sorted.length === 0) {
    return <EmptyState icon="📭" title={emptyLabel} />
  }

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((item, i) => (
        <motion.div key={item.id} {...staggerIndex(i)}>
          <NewsCard item={item} />
        </motion.div>
      ))}
    </div>
  )
}

// Announcements and release notes. Anything happening at a set time lives on the
// Events page instead, so a workshop tomorrow never competes with a changelog.
export function NewsPage() {
  const { t } = useTranslation()
  const { gymId, gymName } = useOutletContext<GymContext>()
  const { gym, global, loading } = useNews(gymId)
  const { seenNews, markNewsSeen } = useSeenNews()
  const [tab, setTab] = useTabParam('tab', NEWS_TABS, 'gym')

  const { gymNews, communityNews, appUpdates } = useMemo(
    () => splitFeed({ gym, global }),
    [gym, global],
  )

  // Only the tab actually open is marked read. Clearing all three on arrival is
  // what the page used to do, and it made the per-tab dots meaningless: the two
  // tabs a member had not looked at went quiet at the same moment as the one
  // they had. Mark from the data rather than from the DOM — HeroUI unmounts the
  // inactive tabs, so reading what is rendered would be a different bug with the
  // same shape.
  const activeIds = useMemo(() => {
    const lists: Record<NewsTab, NewsItem[]> = {
      gym: gymNews,
      global: communityNews,
      updates: appUpdates,
    }
    return lists[tab].map((i) => i.id)
  }, [tab, gymNews, communityNews, appUpdates])
  useEffect(() => {
    if (!loading) markNewsSeen(activeIds)
  }, [loading, activeIds, markNewsSeen])

  return (
    <Page>
      <h1 className="sr-only">{t('news.pageTitle')}</h1>
      {loading ? (
        <div className="flex flex-col gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ChipTabs
            aria-label={t('news.pageTitle')}
            value={tab}
            onChange={setTab}
            options={[
              {
                key: 'gym',
                label: (
                  <TabTitle label={t('news.tabGym')} dot={unseenNewsCount(gymNews, seenNews) > 0} />
                ),
              },
              {
                key: 'global',
                label: (
                  <TabTitle
                    label={t('news.tabGlobal')}
                    dot={unseenNewsCount(communityNews, seenNews) > 0}
                  />
                ),
              },
              {
                key: 'updates',
                label: (
                  <TabTitle
                    label={t('news.tabUpdates')}
                    dot={unseenNewsCount(appUpdates, seenNews) > 0}
                  />
                ),
              },
            ]}
          />
          <div role="tabpanel" aria-label={t('news.pageTitle')}>
            {tab === 'gym' && (
              <NewsList items={gymNews} emptyLabel={t('news.emptyGym', { gym: gymName })} />
            )}
            {tab === 'global' && (
              <NewsList items={communityNews} emptyLabel={t('news.emptyGlobal')} />
            )}
            {tab === 'updates' && (
              <NewsList items={appUpdates} emptyLabel={t('news.emptyUpdates')} />
            )}
          </div>
        </div>
      )}
    </Page>
  )
}
